import React, { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styled, { css, keyframes } from 'styled-components';

const GAP = 8;

// Tooltip is rendered in a portal on document.body with position: fixed. A
// CSS-only absolute tooltip is trapped inside the nearest ancestor stacking
// context and clipped by any ancestor overflow — so it used to disappear
// behind/under cards. Portaling to the body root escapes every ancestor
// stacking context and overflow clip; the box is positioned from the
// trigger's live bounding rect captured on hover/focus.
const Tooltip = ({ text, children, position = 'top' }) => {
    const ref = useRef(null);
    const [rect, setRect] = useState(null);

    const show = useCallback(() => {
        const el = ref.current;
        if (el) setRect(el.getBoundingClientRect());
    }, []);
    const hide = useCallback(() => setRect(null), []);

    if (!text) return children;

    return (
        <>
            <TooltipContainer
                ref={ref}
                onMouseEnter={show}
                onMouseLeave={hide}
                onFocus={show}
                onBlur={hide}
            >
                {children}
            </TooltipContainer>
            {rect && createPortal(
                <TooltipBox className="tooltip-box" data-portal-layer="tooltip" $position={position} style={positionStyle(rect, position)}>
                    {text}
                </TooltipBox>,
                document.body,
            )}
        </>
    );
};

export default Tooltip;

// Fixed-position coordinates anchored to the trigger rect. The transform
// (in TooltipBox) then aligns the box around this anchor point.
function positionStyle(rect, position) {
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    switch (position) {
        case 'bottom': return { top: rect.bottom + GAP, left: cx };
        case 'left': return { top: cy, left: rect.left - GAP };
        case 'right': return { top: cy, left: rect.right + GAP };
        default: return { top: rect.top - GAP, left: cx }; // top
    }
}

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const TooltipContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const TooltipBox = styled.div`
  position: fixed;
  background-color: #1e293b;
  color: #ffffff;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  pointer-events: none;
  z-index: 99999;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  font-family: 'Nunito Sans', sans-serif;
  animation: ${fadeIn} 0.12s ease;

  ${({ $position }) => {
        switch ($position) {
            case 'bottom':
                return css`transform: translate(-50%, 0);`;
            case 'left':
                return css`transform: translate(-100%, -50%);`;
            case 'right':
                return css`transform: translate(0, -50%);`;
            default:
                return css`transform: translate(-50%, -100%);`;
        }
    }}

  &::after {
    content: '';
    position: absolute;
    border-width: 5px;
    border-style: solid;

    ${({ $position }) => {
        switch ($position) {
            case 'bottom':
                return css`bottom: 100%; left: 50%; margin-left: -5px; border-color: transparent transparent #1e293b transparent;`;
            case 'left':
                return css`left: 100%; top: 50%; margin-top: -5px; border-color: transparent transparent transparent #1e293b;`;
            case 'right':
                return css`right: 100%; top: 50%; margin-top: -5px; border-color: transparent #1e293b transparent transparent;`;
            default:
                return css`top: 100%; left: 50%; margin-left: -5px; border-color: #1e293b transparent transparent transparent;`;
        }
    }}
  }
`;
